import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Downshift from 'downshift';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import get from 'platform/utilities/data/get';
import {
  getVhaMedicalFacilityLabel,
  isOtherVhaMedicalFacility,
  OTHER_VHA_MEDICAL_FACILITY_OPTION,
  VHA_FACILITY_STATE_SCHEMA_KEY,
} from '../definitions/facilities';
import {
  getDefaultVhaFacilityState,
  isVhaFacilityStateCode,
  normalizeVhaFacilityState,
} from '../definitions/facilityStates';

export default function VhaFacilityAutosuggestField(props) {
  const {
    uiSchema,
    idSchema,
    schema,
    formData,
    onChange,
    onBlur,
    formContext,
  } = props;
  const uiOptions = uiSchema['ui:options'] || {};
  const {
    classNames: fieldClassNames,
    fallbackStatePath,
    getOptions,
    maxOptions = 150,
    hint,
    placeholder,
    statePath,
    useAddressFallback = true,
  } = uiOptions;

  const id = idSchema.$id;
  const fullFormData = useSelector(state => state.form?.data || {});

  const schemaFacilityState = schema?.[VHA_FACILITY_STATE_SCHEMA_KEY];

  const selectedFacilityState = statePath
    ? get(statePath, fullFormData)
    : undefined;

  const fallbackFacilityState = fallbackStatePath
    ? get(fallbackStatePath, fullFormData)
    : undefined;

  const addressFacilityState = useAddressFallback
    ? getDefaultVhaFacilityState(fullFormData?.address)
    : undefined;
  const facilityState =
    schemaFacilityState ||
    selectedFacilityState ||
    fallbackFacilityState ||
    addressFacilityState;

  const normalizedFacilityState = normalizeVhaFacilityState(facilityState);
  const hasFacilityState = isVhaFacilityStateCode(normalizedFacilityState);
  const selectedLabel = getVhaMedicalFacilityLabel(formData);
  const previousFacilityState = useRef(normalizedFacilityState);

  const [input, setInput] = useState(selectedLabel);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    const previousState = previousFacilityState.current;
    if (previousState && previousState !== normalizedFacilityState) {
      setInput('');
      setSuggestions([]);

      if (formData) {
        onChange(undefined);
      }
    }
    previousFacilityState.current = normalizedFacilityState;
  }, [formData, normalizedFacilityState, onChange]);

  useEffect(() => {
    if (!hasFacilityState) {
      setIsLoading(false);
      setLoadError(false);
      setSuggestions([]);
      return undefined;
    }
    let active = true;
    setIsLoading(true);
    setLoadError(false);
    Promise.resolve(
      getOptions(input, {
        state: normalizedFacilityState,
      }),
    )
      .then(result => {
        if (!active) {
          return;
        }
        const options = result || [];
        const otherOption = options.find(isOtherVhaMedicalFacility);
        const facilityOptions = options
          .filter(option => !isOtherVhaMedicalFacility(option))
          .slice(0, Math.max(maxOptions - (otherOption ? 1 : 0), 0));

        setSuggestions(
          otherOption ? [...facilityOptions, otherOption] : facilityOptions,
        );
      })
      .catch(() => {
        if (!active) {
          return;
        }
        setLoadError(true);
        setSuggestions([OTHER_VHA_MEDICAL_FACILITY_OPTION]);
      })
      .finally(() => {
        if (active) {
          setIsLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [
    getOptions,
    hasFacilityState,
    input,
    maxOptions,
    normalizedFacilityState,
  ]);

  const statusId = `${id}-status`;

  const describedBy = [hint ? `${id}-hint` : null, statusId]
    .filter(Boolean)
    .join(' ');

  const handleInputValueChange = (value, changes) => {
    if (changes?.type !== Downshift.stateChangeTypes.changeInput) {
      return;
    }
    const nextInput = value || '';
    setInput(nextInput);
    if (formData) {
      onChange(undefined);
    }
  };

  const handleChange = selectedItem => {
    if (!selectedItem) return;
    const label = getVhaMedicalFacilityLabel(selectedItem);

    setInput(label);
    onChange(label);
  };

  if (formContext?.reviewMode) {
    return <span>{selectedLabel}</span>;
  }

  return (
    <>
      {hint && (
        <span id={`${id}-hint`} className="usa-hint">
          {hint}
        </span>
      )}
      <Downshift
        onChange={handleChange}
        onInputValueChange={handleInputValueChange}
        inputValue={input}
        selectedItem={formData || null}
        itemToString={getVhaMedicalFacilityLabel}
      >
        {({
          getInputProps,
          getItemProps,
          isOpen,
          highlightedIndex,
          openMenu,
        }) => (
          <div className="autosuggest-container">
            <input
              {...getInputProps({
                id,
                name: id,
                className: classNames('autosuggest-input', fieldClassNames),
                placeholder: hasFacilityState
                  ? placeholder
                  : 'Select a state or territory first',
                autoComplete: 'off',
                readOnly: !hasFacilityState,
                'aria-disabled': !hasFacilityState ? 'true' : undefined,
                'aria-describedby': describedBy,
                'aria-busy': isLoading ? 'true' : undefined,
                onKeyDown: event => {
                  if (!hasFacilityState && event.key !== 'Tab') {
                    event.preventDefault();
                  }
                },
                onFocus: () => {
                  if (hasFacilityState) {
                    openMenu();
                  }
                },
                onClick: () => {
                  if (hasFacilityState) {
                    openMenu();
                  }
                },
                onBlur: isOpen ? undefined : () => onBlur(id, formData),
              })}
            />
            {isOpen && suggestions.length > 0 && (
              <div className="autosuggest-list" role="listbox">
                {suggestions.map((item, index) => (
                  <div
                    key={item.id || item.label}
                    {...getItemProps({
                      item,
                      index,
                    })}
                    role="option"
                    aria-selected={formData === item.label ? 'true' : 'false'}
                    className={classNames('autosuggest-item', {
                      'autosuggest-item-highlighted':
                        highlightedIndex === index,
                      'autosuggest-item-selected': formData === item.label,
                    })}
                  >
                    {item.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Downshift>
      <div id={statusId} role="status" aria-live="polite">
        {isLoading && 'Loading VA facilities…'}
        {loadError &&
          'We couldn’t load the VA facility list. Select “Other” and enter the facility name.'}
      </div>
    </>
  );
}

VhaFacilityAutosuggestField.propTypes = {
  idSchema: PropTypes.shape({
    $id: PropTypes.string.isRequired,
  }).isRequired,
  schema: PropTypes.shape({
    type: PropTypes.string.isRequired,
    [VHA_FACILITY_STATE_SCHEMA_KEY]: PropTypes.string,
  }).isRequired,
  uiSchema: PropTypes.shape({
    'ui:options': PropTypes.shape({
      classNames: PropTypes.string,
      fallbackStatePath: PropTypes.string,
      getOptions: PropTypes.func.isRequired,
      hint: PropTypes.string,
      maxOptions: PropTypes.number,
      placeholder: PropTypes.string,
      statePath: PropTypes.string,
      useAddressFallback: PropTypes.bool,
    }),
  }).isRequired,
  onBlur: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  formContext: PropTypes.shape({
    reviewMode: PropTypes.bool,
  }),
  formData: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
    }),
  ]),
};
